/**
 * Аптеки сети для карты и списка. `open_now` считает сервер в ташкентском
 * времени — по часам браузера аптека открывалась бы в чужом поясе.
 */
import { useQuery } from '@tanstack/react-query'
import type { components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type StoreDto = components['schemas']['Store']

export interface Store {
  id: number
  name: string
  district: string
  address: string
  metro: string
  phone: string
  lat: number | null
  lng: number | null
  is247: boolean
  hours: ({ open: string; close: string } | null)[]
  openNow: boolean
  acceptsPickup: boolean
  acceptsDelivery: boolean
  /** Аптека имеет право отпускать рецептурные препараты. */
  dispensesRx: boolean
}

const WEEK: null[] = [null, null, null, null, null, null, null]

export function toStore(dto: StoreDto): Store {
  return {
    id: dto.id ?? 0,
    name: dto.name,
    district: dto.district,
    address: dto.address,
    metro: dto.metro ?? '',
    phone: dto.phone ?? '',
    lat: dto.lat ?? null,
    lng: dto.lng ?? null,
    is247: dto.is_24_7 ?? false,
    hours: WEEK.map((_, i) => {
      const day = dto.hours?.[i]
      return day?.open && day.close ? { open: day.open, close: day.close } : null
    }),
    openNow: dto.open_now ?? false,
    acceptsPickup: dto.accepts_pickup ?? true,
    acceptsDelivery: dto.accepts_delivery ?? false,
    dispensesRx: dto.dispenses_rx ?? false,
  }
}

export interface StoresQuery {
  q?: string
  openNow?: boolean
  is247?: boolean
}

export function useStores(query: StoresQuery = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.stores, query],
    queryFn: async () => {
      const page = await api.get<{ items?: StoreDto[] }>('/stores', {
        query: {
          q: query.q || undefined,
          open_now: query.openNow || undefined,
          is_24_7: query.is247 || undefined,
        },
      })
      return (page.items ?? []).map(toStore)
    },
  })
}
